import { Fragment, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container } from "reactstrap";
import { Search, User, Star, AlertTriangle } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { searchVehiclesByPlate } from "../../utils/vehicleApi";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, StatusBadge, Loader as RLoader, TablePagination, usePagination } from "../reporting/ReportingShared";

const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "10px 20px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const plateChip = {
  display: "inline-block", padding: "5px 12px", borderRadius: 6, fontSize: 15, fontWeight: 700,
  fontFamily: "monospace", letterSpacing: 1.5, background: C.white,
  border: `2px solid ${C.text}`, color: C.text,
};

// Look a customer up by their plate.
//
// Matches on plateNormalized — the same canonical form the ANPR pipeline produces
// from a camera read — so a plate copied off a drive-thru snapshot resolves here too.
// Partial matches work, for when someone only caught half of it.
//
// Read-only by design: admins can see a customer's vehicles but not edit or delete
// them. A support question is answered by looking, not by rewriting their data.
const PlateSearch = () => {
  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((s) => s.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const [plate, setPlate] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // A partial plate can match up to the server's 25-row cap, and each match renders a
  // full card — page them rather than dumping the lot.
  const resultsPg = usePagination(results || [], 5);

  // Plates are deliberately NOT globally unique — spouses, family and company cars
  // legitimately share one, and a used-car buyer must be able to register a plate the
  // previous owner still has saved. But a shared plate is worth surfacing: it is the
  // one case where staff could walk an order to the wrong car.
  const sharedPlates = useMemo(() => {
    const byPlate = new Map();
    (results || []).forEach((r) => {
      const k = r.vehicle.plateNormalized;
      byPlate.set(k, (byPlate.get(k) || 0) + 1);
    });
    return new Set([...byPlate.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [results]);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!plate.trim()) return;
    setLoading(true);
    const data = await searchVehiclesByPlate(plate.trim(), selectedMerchant);
    setResults(data?.results || []);
    setLoading(false);
  };

  if (!selectedMerchant || selectedMerchant === "all") {
    return (
      <Fragment>
        <Breadcrumb title="Plate Search" parent="Vehicles" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RCard><div style={{ padding: 24, color: C.textMed }}>Please select a merchant.</div></RCard>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Plate Search" parent="Vehicles" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <form onSubmit={runSearch} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder="ABJ1234 — or any part of it"
                style={{
                  padding: "11px 16px", borderRadius: 10, border: `1.5px solid ${C.border}`,
                  fontSize: 15, width: 300, outline: "none", fontFamily: "monospace",
                  letterSpacing: 1.5, fontWeight: 600, textTransform: "uppercase",
                }}
                onFocus={(e) => (e.target.style.borderColor = C.coral)}
                onBlur={(e) => (e.target.style.borderColor = C.border)}
              />
              <button type="submit" style={coralBtn} disabled={loading}>
                <Search size={15} /> {loading ? "Searching…" : "Search"}
              </button>
              <span style={{ fontSize: 12, color: C.textLight }}>
                Spaces and dashes are ignored
              </span>
            </form>
          </RCard>

          {loading && <RLoader />}

          {!loading && results !== null && results.length === 0 && (
            <RCard>
              <div style={{ padding: 32, textAlign: "center", color: C.textLight }}>
                No vehicle matches "{plate}" for this merchant.
              </div>
            </RCard>
          )}

          {!loading && results?.length > 0 && (
            <div style={{ fontSize: 12, color: C.textMed, margin: "0 0 12px 2px" }}>
              {results.length} match{results.length === 1 ? "" : "es"}
              {results.length >= 25 && " (showing the first 25 — narrow the plate for fewer)"}
            </div>
          )}

          {!loading && resultsPg.paged.map(({ vehicle, customer, recentOrders }) => (
            <RCard key={vehicle.id}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={plateChip}>{vehicle.plate}</div>
                  <div style={{ marginTop: 6, fontSize: 15, direction: "rtl", textAlign: "center", color: C.textMed, fontWeight: 600 }}>
                    {vehicle.plateArabic}
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                    {vehicle.color?.hex && (
                      <span style={{
                        display: "inline-block", width: 16, height: 16, borderRadius: 4,
                        background: vehicle.color.hex, border: `1px solid ${C.border}`,
                      }} />
                    )}
                    <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                      {[vehicle.color?.name, vehicle.make?.name, vehicle.model?.name].filter(Boolean).join(" ") || "—"}
                    </span>
                    {vehicle.isDefault && (
                      <Badge color={C.coral} bg={C.coralLight}>
                        <Star size={10} style={{ marginRight: 3, verticalAlign: -1 }} />Default
                      </Badge>
                    )}
                  </div>
                  {vehicle.nickname && (
                    <div style={{ fontSize: 13, color: C.textMed }}>"{vehicle.nickname}"</div>
                  )}
                  {sharedPlates.has(vehicle.plateNormalized) && (
                    <div style={{
                      marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6,
                      background: C.orangeLight, border: `1px solid ${C.orange}44`, borderRadius: 6,
                      padding: "4px 9px", fontSize: 11, fontWeight: 600, color: C.orange,
                    }}>
                      <AlertTriangle size={11} />
                      Shared plate — more than one customer has registered it
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
                    Registered {vehicle.createdAt ? new Date(vehicle.createdAt).toLocaleDateString() : "-"}
                  </div>
                </div>

                <div style={{
                  minWidth: 220, background: C.bg, borderRadius: 10, padding: "12px 16px",
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                    <User size={11} style={{ verticalAlign: -1, marginRight: 4 }} />Customer
                  </div>
                  {customer ? (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{customer.name || "—"}</div>
                      <div style={{ fontSize: 12, color: C.textMed }}>{customer.phoneNo}</div>
                      {customer.email && <div style={{ fontSize: 12, color: C.textLight }}>{customer.email}</div>}
                      <a href={`/reporting/customer-detail/${customer.id}`}
                        style={{ fontSize: 12, color: C.coral, fontWeight: 600, textDecoration: "none" }}>
                        View customer →
                      </a>
                    </>
                  ) : <div style={{ fontSize: 13, color: C.textLight }}>Account removed</div>}
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                Recent orders in this vehicle
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead><tr>
                    <th style={tbl.th}>Ref</th><th style={tbl.th}>Branch</th>
                    <th style={tbl.th}>Type</th><th style={tbl.th}>Status</th>
                    <th style={tbl.th}>Total</th><th style={tbl.th}>Date</th>
                  </tr></thead>
                  <tbody>
                    {recentOrders.length === 0 && (
                      <tr><td colSpan={6} style={{ ...tbl.td, textAlign: "center", color: C.textLight, padding: 20 }}>
                        No orders placed with this vehicle yet
                      </td></tr>
                    )}
                    {recentOrders.map((o) => (
                      <tr key={o.id} className="rpt-row">
                        <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>#{o.reference}</td>
                        <td style={{ ...tbl.td, color: C.textMed }}>{o.branchName || "-"}</td>
                        <td style={{ ...tbl.td, color: C.textMed }}>{o.orderType || "-"}</td>
                        <td style={tbl.td}><StatusBadge s={o.status} /></td>
                        <td style={tbl.td}>{o.grandTotal ?? "-"}</td>
                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                          {o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </RCard>
          ))}

          {!loading && results?.length > resultsPg.limit && (
            <RCard>
              <TablePagination total={resultsPg.total} page={resultsPg.page} limit={resultsPg.limit}
                onPageChange={resultsPg.setPage} onLimitChange={resultsPg.setLimit} />
            </RCard>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default PlateSearch;
